<template>
	<div class="card shadow-sm border p-5 mb-5">
		<div class="d-flex align-items-center">
			<i :class="props.icon" style="font-size: 30px"></i>
			<h4 class="text-uppercase m-0">{{ props.title }}</h4>
		</div>
		<div class="d-flex justify-content-center">
            <webrtc_video_player
                :key="player_key"
                ref="video_player_component"
                :auto="false"
                :height="props.streaming_details?.height"
                :width="props.streaming_details?.width"
                :source_url="props.streaming_details?.streaming_url"
                :controls="true"
                :streaming_auth_credential="props.streaming_auth_credential"/>
		</div>
		<div v-if="props.commands_enabled" class="row btn-group p-2" role="group">
			<button
				@click="startTransmition()"
				id="start-transmition"
				class="col-12 col-md-4 btn-ws btn-lg btn-block btn mt-2 btn-success">
				Ligar
			</button>
			<button
				@click="restartTransmition()"
				id="restart-transmition"
				class="col-12 col-md-4 btn-ws-action btn-lg btn-block btn mt-2 btn-primary">
				Reiniciar
			</button>
			<button
				@click="stopTransmition()"
				id="close-transmition"
				class="col-12 col-md-4 btn-ws-action btn-lg btn-block btn mt-2 btn-danger">
				Desligar
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
    import { onBeforeUnmount, onMounted, inject, ref, watch } from 'vue';
	import axios from 'axios';
    import webrtc_video_player from '../Widgets/webrtc_video_player.vue';

    const props = defineProps({
		title: { type: String, required: true },
		device: { type: String, required: true },
		icon: { type: String, required: true },
		enrollment_test_id: { type: String, required: true },
		computer_uid: { type: String, required: true },
		commands_urls: { type: Object, required: true },
        streaming_details: { type: Object as () => StreamingDetails, required: true },
        streaming_already_started: {type: Boolean, required: true},
        commands_enabled: { type: Boolean, required: false, default: true },
        streaming_auth_credential: { type: String, required: true },
    });

    interface StreamingDetails {
        streaming_url: string;
        width: number;
        height: number;
    }

	const streaming_on = ref(props.streaming_already_started);

	defineExpose({ startTransmition, stopTransmition, streaming_on });

    const video_player_component = ref<InstanceType<typeof webrtc_video_player> | null>(null);

    const player_key = ref(1)

    async function startTransmition() {
		await axios
			.post(props.commands_urls.start, {
				uid: props.computer_uid,
				enrollment_test_id: props.enrollment_test_id,
			})
			.then((response) => {
				//@ts-ignore
				toastr.success(`Ligando o dispositivo:${props.device}`);
                streaming_on.value = true
                setTimeout(() => video_player_component.value?.start(), 5000)
			})
			.catch((response) => {
				//@ts-ignore
				toastr.error(
					`Não foi possível ligar o dispositivo: ${props.device}, erro: ${response.response.data.message}`
				);
			});
	}

	async function stopTransmition() {
        await video_player_component.value?.stop()

        await axios
			.post(props.commands_urls.stop, {
				uid: props.computer_uid,
				enrollment_test_id: props.enrollment_test_id,
			})
			.then((response) => {
				//@ts-ignore
				toastr.success(
					`Parando a transmissão do dispositivo:${props.device}`
				);
				streaming_on.value = false;
                player_key.value++
			})
			.catch((response) => {
				//@ts-ignore
				toastr.error(
					`Não foi possível parar a transmissão do dispositivo:${props.device}, erro: ${response.response.data.message}`
				);
			});
	}

	async function restartTransmition() {
        await video_player_component.value?.stop()

        axios
			.post(props.commands_urls.restart, {
				uid: props.computer_uid,
				enrollment_test_id: props.enrollment_test_id,
			})
			.then((response) => {
				//@ts-ignore
				toastr.success(
					`Reiniciando a transmissão do dispositivo:${props.device}`
				);
                player_key.value++
                setTimeout(() => video_player_component.value?.start(), 5000)
            })
			.catch((response) => {
				//@ts-ignore
				toastr.success(
					`Não foi possível reiniciar a transmissão do dispositivo:${props.device}, erro: ${response.response.data.message}`
				);
			});
	}

    // Initial start runs in onMounted so the child <webrtc_video_player>
    // ref is already bound. The watcher handles post-mount transitions
    // (status pill flipping to online via WS after the page is open) —
    // not configured `immediate` because Vue invokes the immediate callback
    // synchronously during setup(), which is before refs populate.
    onMounted(() => {
        if (props.streaming_already_started) {
            streaming_on.value = true;
            video_player_component.value?.start();
        }
    });

    watch(() => props.streaming_already_started, (is_started) => {
        if (!is_started) return;
        streaming_on.value = true;
        video_player_component.value?.start();
    });

    // Auto-stop sweep on the server kills the producer ffmpeg without going
    // through the local stop button — without this the WebRTC player would
    // keep retrying. Bumping player_key forces a clean remount.
    const emitter: any = inject('emitter');
    function handleAutoStopped() {
        if (!streaming_on.value) return;
        video_player_component.value?.stop();
        streaming_on.value = false;
        player_key.value++;
    }
    emitter?.on('stream_auto_stopped', handleAutoStopped);
    onBeforeUnmount(() => {
        emitter?.off('stream_auto_stopped', handleAutoStopped);
    });
</script>
